import { DialogConfig } from '@agent-ds/shared/components/dialog/dialog-config';
import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { StudentAIInterviewType } from '@agent-ds/shared/enums';
import { StudentAIInterviewResult, StudentDetail } from '@agent-ds/shared/interfaces';
import { FormMeta, RowMeta } from '@agent-ds/shared/models';
import { DynamicFieldService } from '@agent-ds/shared/services';
import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ag-student-gps-academic-view-info-dialog',
  templateUrl: './student-gps-academic-view-info-dialog.component.html',
  styleUrls: ['./student-gps-academic-view-info-dialog.component.scss'],
})
export class StudentGPSAcademicViewInfoDialogComponent implements OnInit, OnDestroy {
  constructor(
    private dynamicService: DynamicFieldService,
    public dialog: DialogRef,
    public config: DialogConfig<{ info: StudentAIInterviewResult; student: StudentDetail; }>,
  ) {}
  student: StudentDetail;
  type: StudentAIInterviewType;
  entryType: string;
  createdAt: string;
  updatedAt: string;
  saving = false;
  pageTitle: string;

  studentGPSAcademicDataModel: {} = {};

  studentGPSAcademicMeta: FormMeta;
  private fieldSubscription: Subscription;

  ngOnInit() {
    this.type = this.config.data.info.type;
    this.student = this.config.data.student;
    this.studentGPSAcademicDataModel = this.config
      && this.config.data
      && this.config.data.info
      && this.config.data.info.dynamicData
      || {};

    this.entryType = this.type === StudentAIInterviewType.GPS ? 'GPS' : 'PeopleX';
    this.createdAt = moment(this.config.data.info.createdAt).format('YYYY-MM-DD');
    this.updatedAt = moment(this.config.data.info.updatedAt).format('YYYY-MM-DD');
    this.pageTitle = this.type === StudentAIInterviewType.GPS ? 'GPS' : 'AI面談・AI面接対策';

    this.fieldSubscription = this.dynamicService.fieldUpdateEvent.subscribe(() => {
      this.initFieldsMeta();
    });
  }

  ngOnDestroy() {
    if (this.fieldSubscription) {
      this.fieldSubscription.unsubscribe();
      this.fieldSubscription = null;
    }
  }

  private initFieldsMeta() {
    const rows: RowMeta[] = this.type === StudentAIInterviewType.GPS ? this.getGPSRows() : this.getPeopleXRows();
    const title: string = this.type === StudentAIInterviewType.GPS ? 'GPS情報' : 'PeopleX情報';

    this.studentGPSAcademicMeta = {
      disabled: true,
      initTouched: true,
      groups: [
        {
          title: title,
          class: 'form__group--green-title-border',
          rows: rows,
        },
      ],
    };
  }

  private getGPSRows(): RowMeta[] {
    return [
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExaminationDate')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsCandidateId'), null, 'full'),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityOverviewRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCriticalThinkingSkillsRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCriticalThinkingSkillsScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCollaborativeThinkingRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCollaborativeThinkingScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCreativeThinkingAbilityRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsThinkingAbilityCreativeThinkingAbilityScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeResilienceRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeResilienceScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeEmotionalControlRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeEmotionalControlScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeQuickRecoveryRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeQuickRecoveryScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeRespondsCalmlyRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeRespondsCalmlyScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeLeadershipRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeLeadershipScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeTakeLeadRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeTakeLeadScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeChallengeTheUnknownRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeChallengeTheUnknownScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudePersistenceRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudePersistenceScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeCollaborationRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeCollaborationScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeUnderstandingOfOthersRank')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeUnderstandingOfOthersScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeProactivenessForInteractingWithOthersRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsAttitudeProactivenessForInteractingWithOthersScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceOverviewRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceOverviewScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSelfManagementRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSelfManagementScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceChallengingRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceChallengingScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceContinueRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceContinueScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDealingWithStressRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDealingWithStressScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceInterpersonalRelationshipsRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceInterpersonalRelationshipsScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceEmbracingDiversityRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceEmbracingDiversityScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceBuildRelationshipsRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceBuildRelationshipsScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDiscussRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDiscussScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperiencePlanningAndExecutionRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'gpsExperiencePlanningAndExecutionScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSetupAssignmentRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceSetupAssignmentScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDevelopSolutionRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceDevelopSolutionScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceExecuteAndVerifyRank')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'gpsExperienceExecuteAndVerifyScore')),
    ];
  }

  private getPeopleXRows(): RowMeta[] {
    return [
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexAIInterviewDate')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexAIInterviewName'), null, 'full'),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationOverviewRank'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationOverviewScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationOverview'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationStrengths'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexComprehensiveEvaluationImprovementPoints'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageOverviewScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageExpressivePowerScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageUnderstandingAbilityScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexLanguageLogicalThinkingAbilityScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonverbalOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonVerbalActionsExpressionsScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonVerbalLanguageMannersScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexNonVerbalToneSpeedScore')),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesWillingnessGrowScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesCareerVisionScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexAttitudeValuesSenseResponsibilityScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillOverviewScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillProblemFindingAndSolvingSkillsScore'),
      ),
      ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillTeamworkScore')),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillEntrainmentAndPropulsionForceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexKnowledgeSkillManagementExperienceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsOverviewScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsIndependenceAspirationsScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsStressToleranceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexBehavioralCharacteristicsEffortPersistenceScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexDegreeOfInterestUnderstandingOfCompanyScore'),
      ),
      ...this.dynamicService.getFormRows(
        this.dynamicService.getDefinition('student_ai_interview', 'peoplexConsistencyMotivationForApplyingAndReasonsForLeavingScore'),
      ),
    ];
  }

  public close(): void {
    this.dialog.close();
  }
}
